import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { FlowModel, FlowNodeTypeModel, FlowStepModel } from "../../../models";
import { FlowStepOutputMapping } from "./FlowStepOutputMapping";
import { FlowStepSettings } from "./FlowStepSettings";
import { FlowStepInterface } from "./FlowStepInterface";
import { FlowStepFieldsUpdateButton } from "./FlowStepFieldsUpdateButton";

type Panel = "in" | "out" | "mapping" | "settings";
const panels: { name: string; value: Panel; align?: "right" }[] = [
  { name: "Input", value: "in" },
  { name: "Output", value: "out" },
  { name: "Mapping", value: "mapping" },
  { name: "Settings", value: "settings", align: "right" },
];

export function FlowStepEditor({
  flow,
  nodeTypes,
  step,
  onSetStep,
}: {
  flow: FlowModel;
  nodeTypes: FlowNodeTypeModel[];
  step: FlowStepModel;
  onSetStep: (step: FlowStepModel) => void;
}) {
  const [visiblePanel, setVisiblePanel] = useState<Panel>("in");

  function setNodeType(nodeTypeName: string) {
    const nodeType = nodeTypes.find(
      (nodeTypeItem) => nodeTypeItem.name === nodeTypeName
    );
    onSetStep({
      ...step,
      node_type: nodeTypeName,
      automated: nodeType?.automated || false,
      step_data: { in: nodeType?.in || [], out: nodeType?.out || [] },
      next_statuses: nodeType?.outcomes || [],
      settings:
        nodeType?.settings.map((item) => ({ id: item.id, value: "" })) || [],
    });
  }

  const stepSettings = nodeTypes
    .find((item) => item.name === step.node_type)
    ?.settings?.map((ns) => ({
      ...ns,
      value: step.settings.find((ss) => ss.id === ns.id)?.value || "",
    }));

  return (
    <div className="flex flex-1 gap-2">
      <div className="flex flex-col w-1/4 gap-4">
        <TextField
          label="Name"
          value={step.name}
          size="small"
          onChange={(e) => onSetStep({ ...step, name: e.target.value })}
        />
        <TextField
          label="Status ID"
          value={step.status}
          size="small"
          onChange={(e) => onSetStep({ ...step, status: e.target.value })}
        />
        <Autocomplete
          freeSolo
          options={flow.statuses.filter((status) => status)}
          value={step.status_group}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Status group"
              onChange={(e) =>
                onSetStep({ ...step, status_group: e.target.value })
              }
            />
          )}
        />
        <FormControl>
          <InputLabel>Type</InputLabel>
          <Select
            style={{
              backgroundColor: nodeTypes.find(
                (item) => item.name === step.node_type
              )?.color,
            }}
            label="Type"
            size="small"
            value={step.node_type}
            onChange={(e) => setNodeType(e.target.value)}
          >
            {nodeTypes.map((nodeType) => (
              <MenuItem
                sx={{ whiteSpace: "normal" }}
                key={nodeType.name}
                value={nodeType.name}
              >
                <div
                  style={{ backgroundColor: nodeType.color }}
                  className="flex gap-4 p-2 rounded-md "
                >
                  <img
                    className="w-8"
                    src={
                      "/assets/images/flows/node-types/" +
                      nodeType.icon +
                      ".svg"
                    }
                    alt={nodeType.icon}
                  />
                  <div className="flex flex-col w-72">
                    <h3 className="text-wrap">{nodeType.title}</h3>
                    <p className="text-xs text-wrap">{nodeType.description}</p>
                  </div>
                </div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={step.first_step}
              onChange={(e) =>
                onSetStep({ ...step, first_step: e.target.checked })
              }
            />
          }
          label="First step"
        />
        <TextField
          multiline
          rows={3}
          label="Routine"
          value={step.routine}
          size="small"
          onChange={(e) => onSetStep({ ...step, routine: e.target.value })}
        />
        <TextField
          label="Manual Worktime (minutes)"
          inputProps={{ type: "number" }}
          value={step.manual_worktime}
          size="small"
          onChange={(e) =>
            onSetStep({ ...step, manual_worktime: e.target.value })
          }
        />
        <FlowStepFieldsUpdateButton
          step={step}
          definition={nodeTypes.find((item) => item.name === step.node_type)}
          onClick={onSetStep}
        />
        {/* <div className="mt-auto">
          <NextSteps flow={flow} step={step} onStatusClick={(status) => {}} onStatusDelete={() => {}} />
        </div> */}
      </div>
      <div className="flex flex-col flex-1 border-l-2 pl-2 gap-4">
        <div className="flex gap-4 border-b-2">
          {panels.map((item) => (
            <button
              key={item.value}
              className={`px-4 py-1 rounded-t-md ${
                visiblePanel === item.value
                  ? "font-bold text-white bg-black"
                  : ""
              } ${item.align === "right" ? "ml-auto" : ""}`}
              onClick={() => setVisiblePanel(item.value)}
            >
              {item.name}
            </button>
          ))}
        </div>
        {visiblePanel === "in" ? (
          <FlowStepInterface
            flow={flow}
            step={step}
            setStep={onSetStep}
            interfaceType="in"
          />
        ) : visiblePanel === "out" ? (
          <FlowStepInterface
            flow={flow}
            step={step}
            setStep={onSetStep}
            interfaceType="out"
          />
        ) : visiblePanel === "mapping" ? (
          <FlowStepOutputMapping
            key={step.status}
            steps={flow.steps}
            step={step}
            onSetNextStatuses={(statuses) =>
              onSetStep({ ...step, next_statuses: statuses })
            }
          />
        ) : visiblePanel === "settings" ? (
          <FlowStepSettings
            settings={stepSettings}
            onSetSettings={(settings) =>
              onSetStep({ ...step, settings: settings })
            }
          />
        ) : null}
      </div>
    </div>
  );
}
